import '@stripe/stripe-js'; // Adds Stripe to the global scope so it's fraud detection can work
import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { log } from '@electricjs/arc';

import { worker } from '@/mocks/browser';
import { MockAuth0Provider } from '@/mocks/components/MockAuth0Provider';
import { isMockMode } from '@/mocks/utils/helpers';
import { initDatadog } from '@/services/datadog';

import App from './App';
import { installModalFocusLockBypassForConnectUI } from './helpers/modalFocusLockBypassForConnectUI';
import { markAuthRedirectReloadStarted } from './helpers/authRedirectReload';

installModalFocusLockBypassForConnectUI();
import { startFetchIntervals } from './helpers/fetchAppVersion';
import { getIsolvedAuthorizationParams } from './helpers/getIsolvedAuthorizationParams';
import { validateReturnToUrl } from './helpers/validateReturnToUrl';
import stripe from './services/stripe';
import '@/i18n'; // Initialize i18n
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/700.css';

/**
 * Development mode with mock service
 *   - When running in development mode with mock enabled (`npm run dev:mock`)
 *   - The webworker will intercept all API requests and return the mock result if there is a handler for that request
 *   - Requests without handler will be warned, so we can implement the missing ones
 *   - Auth0 is replaced by MockAuth0Provider (always logged in as the mocked user)
 *   - Feature flags come from `MOCKED_FEATURE_FLAGS` (src/constants/mockMode.ts) and are bootstrapped in LDProvider (see App.tsx)
 */

if (isMockMode) {
  // Await worker.start() so React doesn't render before MSW is ready to intercept.
  await worker.start({ onUnhandledRequest: 'error' }); // always shows unhandled requests as "error", so it fails immediately and let the UI finishes its rendering (accounting the feature flags)
}

// Don't initialize Datadog on public signup pages
const isSignupPage =
  window.location.pathname === '/signup' ||
  window.location.pathname === '/trinet';

if (!isSignupPage) {
  if (
    window._env_?.VITE_DATADOG_APPLICATION_ID &&
    window._env_?.VITE_DATADOG_CLIENT_TOKEN
  ) {
    initDatadog({
      applicationId: window._env_.VITE_DATADOG_APPLICATION_ID,
      clientToken: window._env_.VITE_DATADOG_CLIENT_TOKEN,
      version: window._env_?.VITE_APP_VERSION,
      env: window._env_?.VITE_ENV_NAME || 'local',
    });
  } else {
    console.warn('DataDog not initialized, missing environment variables');
  }
}
/** Fetch App Version Interval Setup */
startFetchIntervals();

try {
  const stripeService = await stripe;
  if (!stripeService) {
    log.warn({
      origin: 'main.tsx',
      message: 'Stripe not initialized, missing public key',
      appName: 'IT Management',
    });
  }
} catch (error) {
  console.error('Stripe initialization failed', error);
  window.DD_RUM?.addError(error, {
    location: 'main.tsx',
  });
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

const root = createRoot(container);
root.render(
  <StrictMode>
    {isMockMode ? (
      <MockAuth0Provider>
        <App />
      </MockAuth0Provider>
    ) : (
      <Auth0Provider
        clientId={window._env_?.VITE_AUTH0_CLIENT_ID}
        domain={window._env_?.VITE_AUTH0_DOMAIN}
        authorizationParams={{
          redirect_uri: window._env_?.VITE_AUTH0_CALLBACK_URL,
          audience: window._env_?.VITE_AUTH0_AUDIENCE,
          scope:
            'openid email profile read:current_user update:current_user_identities',
          // We use this to pass the redirect URL to Auth0 for Turbine users
          custom_redirect: `${window.location.pathname}${window.location.search}`,
          // Forward isolved's SSO params (connection + tenantId/clientId/globalId) to Auth0
          ...getIsolvedAuthorizationParams(window.location.search),
        }}
        onRedirectCallback={(appState: AppState | undefined) => {
          if (appState && appState.returnTo) {
            const validatedUrl = validateReturnToUrl(appState.returnTo);
            // window.location.href doesn't navigate synchronously, so mark the
            // reload as in-progress first - App checks this to keep showing the
            // spinner instead of painting the authenticated UI for a frame or
            // two while the browser is still processing the navigation.
            markAuthRedirectReloadStarted();
            window.location.href = validatedUrl;
          }
        }}
        skipRedirectCallback={window.location.pathname.includes(
          '/integrations/oauth/callback'
        )}
        cacheLocation="localstorage"
        useRefreshTokens={true}
        // Without this, a missing/invalid refresh token silently falls back to an
        // iframe-based silent auth check that never resolves when third-party
        // cookies are blocked (e.g. Safari ITP), hanging the app on a spinner with
        // no visible error. Disabling the fallback makes that failure throw instead,
        // so useStoreLoggedUser can catch it and redirect to a real login.
        useRefreshTokensFallback={false}>
        <App />
      </Auth0Provider>
    )}
  </StrictMode>
);
